import { notify } from "@kyvg/vue3-notification";
import {
  getCookie,
  setCookie,
  parseAccessToken,
  isTokenExpired,
  clearUserLogin,
  dateToUtcStartOfDay,
  dateToUtcEndOfDay,
} from "./helpers";
import router from "./router";
import { useAuthStore } from "@/stores/auth/session";
import { combineWithTimeout } from "@/utils/abort";
import { gatedFetch } from "@/utils/routeRateLimiter";

const fetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> =>
  gatedFetch(input, init, () =>
    globalThis.fetch(input, {
      ...init,
      signal: combineWithTimeout(init?.signal ?? undefined),
    }),
  );
import { ApiHttpError } from "@/utils/apiHttpError";
import { mergeGraphSettings } from "@/utils/graphSettings";
import {
  HostType,
  HostsSummary,
  HostGroupType,
  FlowType,
  PatchFlowType,
  BlacklistItemType,
  ExternalHostType,
  UserType,
  SegmentType,
  AuthProvider,
  ProviderType,
  AuthRequest,
  RegisterRequestWithRoles,
  FirewallEvent,
  FirewallEventsFetchOptions,
  FirewallEventsResponse,
  KprobeEvent,
  ProcessEvent,
  NetflowEvent,
  NotificationRule,
  NotificationTransport,
  Alert,
  FirewallType,
  FlowTemplate,
  AgentParameters,
  ConversationItem,
  AlertLevel,
  AlertClass,
  ExcludeType,
  FirewallExclude,
  UserParameters,
  emptyUserParameters,
  Recommendations,
  ListRecommendationsParams,
  HostTag,
  clusterGroup,
  OperationLogs,
  ConditionsFlows,
  AcceptRecommendationsPayload,
  HostInterfaceType,
  FuzzySearchRequest,
  FuzzySearchItem,
  AiChatDTO,
  LicenseInfo,
} from "./types";

export type ErrorType = {
  code: number;
  details: [];
  message: string;
};

export type AuthResponse = {
  capabilites: string[];
  jwt_pair: {
    access: string;
    refresh: string;
  };
  roles: string[];
};

export type AddUserResponse = {
  user_id: string;
};

let refreshInFlight: Promise<void> | null = null;

/**
 * Refresh the access token from the refresh cookie, deduping concurrent calls.
 * Resolves once a fresh access token is set; rejects the same way
 * `issueNewToken` does (`{ message: "unable to authenticate" }` when the
 * refresh token is missing or rejected by the backend).
 */
export function refreshAccessToken(): Promise<void> {
  const refreshToken = getCookie("refresh_token");
  if (!refreshToken) {
    return Promise.reject({ message: "unable to authenticate" });
  }
  if (!refreshInFlight) {
    refreshInFlight = api
      .issueNewToken(refreshToken)
      .then(() => undefined)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

export async function checkAccessToken(
  reject: (reason?: any) => void,
): Promise<boolean> {
  const parsedJWT = parseAccessToken();
  const refreshToken = getCookie("refresh_token");
  if ((parsedJWT == null || isTokenExpired(parsedJWT)) && refreshToken) {
    // Await the (deduped) refresh so the upcoming request carries a fresh
    // token instead of racing it with a stale one (→ 401 "Token is not valid").
    try {
      await refreshAccessToken();
    } catch (e: any) {
      if (e?.message == "unable to authenticate") {
        router.push("/login");
        reject("unable to authenticate");
        return false;
      }
      notify({
        type: "error",
        text: e?.message + (e?.description ? `(${e.description})` : ""),
      });
    }
  }

  const accessToken = getCookie("access_token");
  if (accessToken == null) {
    reject("no access token provided");
    return false;
  }
  return true;
}

export function getUserRoles(): string[] {
  const parsedJWT = parseAccessToken();
  if (parsedJWT == null) return [];
  return parsedJWT.roles;
}

async function processError(resp: Response, reject: (reason?: any) => void) {
  try {
    if (resp.headers.get("Content-Type")?.includes("application/json")) {
      const json = await resp.json();
      reject(
        new ApiHttpError(resp.status, {
          code: json.code,
          message: json.message,
          details: json.details,
        }),
      );
    } else {
      const text = await resp.text();
      reject(new ApiHttpError(resp.status, { message: text }));
    }
  } catch (err) {
    reject(err);
  }
}

// Builds the query string for the unified flows / flows_recommendations
// `listall` endpoints. `expand` resolves group/host/agent ids to their
// interfaces; otherwise ids are matched literally. `strict` is forced off so
// that unknown ids yield an empty result instead of an error (preserving the
// behaviour of the removed per-field search endpoints).
function listallQuery(
  ids: string[],
  expand: boolean,
  extra?: Record<string, string>,
): string {
  const params = new URLSearchParams();
  for (const id of ids) params.append("endpoints", id);
  if (expand) params.set("expand", "true");
  params.set("strict", "false");
  if (extra) for (const [k, v] of Object.entries(extra)) params.set(k, v);
  return params.toString();
}

type AlertsWithReadMarkParams = {
  agentId?: string;
  limit?: number;
  offset?: number;
  fromDttm?: string;
  toDttm?: string;
  level?: AlertLevel;
  alertClass?: AlertClass;
  description?: string;
  onlyUnread?: boolean;
  fwType?: FirewallType;
};

function buildAlertsWithReadMarkQuery(q: AlertsWithReadMarkParams): string {
  const params: Record<string, string> = {};
  if (q.agentId !== undefined) params["agent"] = q.agentId;
  if (q.limit !== undefined) params["limit"] = q.limit.toString();
  if (q.offset !== undefined) params["offset"] = q.offset.toString();
  if (q.fromDttm !== undefined)
    params["from_dttm"] = dateToUtcStartOfDay(q.fromDttm);
  if (q.toDttm !== undefined) params["to_dttm"] = dateToUtcEndOfDay(q.toDttm);
  if (q.level !== undefined) params["alert_level"] = q.level;
  if (q.alertClass !== undefined) params["alert_class"] = q.alertClass;
  if (q.description) params["alert_description"] = q.description;
  if (q.onlyUnread !== undefined)
    params["only_not_marked_as_read"] = q.onlyUnread.toString();
  if (q.fwType !== undefined) params["fw_type"] = q.fwType;
  return new URLSearchParams(params).toString();
}

async function requestAlertsWithReadMark(
  q: AlertsWithReadMarkParams,
  signal?: AbortSignal,
): Promise<{ items: Alert[]; total_count: number }> {
  return new Promise(async (resolve, reject) => {
    if (!(await checkAccessToken(reject))) return;
    const query = buildAlertsWithReadMarkQuery(q);
    try {
      const resp = await fetch(
        `/api/v1/events/alerts/with-read-mark?${query}`,
        { signal },
      );
      if (!resp.ok) processError(resp, reject);
      else {
        const json = await resp.json();
        const respAlerts: Alert[] = Object.values(
          json.items as { alert: Alert; marked_as_read: boolean }[],
        ).map((v) => ({
          ...v.alert,
          marked_as_read: v.marked_as_read,
        }));
        resolve({ items: respAlerts, total_count: json.total_count });
      }
    } catch (e) {
      reject(e);
    }
  });
}

export const api = {
  async logout(token: string, fallbackToken?: string): Promise<void> {
    const resp = await fetch("/api/v1/logout", {
      method: "POST",
      credentials: "omit",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (resp.ok) return;
    const error = await new Promise<unknown>((resolve) => {
      void processError(resp, resolve);
    });
    if (
      error instanceof ApiHttpError &&
      error.status === 401 &&
      error.apiCode === 16
    ) {
      if (fallbackToken && fallbackToken !== token) {
        return api.logout(fallbackToken);
      }
      return;
    }
    throw error;
  },
  async issueNewToken(refreshToken: string, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      try {
        const resp = await fetch("/api/v1/refresh", {
          method: "POST",
          credentials: "omit",
          body: JSON.stringify({ token: refreshToken }),
          signal,
        });
        if (!resp.ok) {
          if (resp.status !== 401) {
            await processError(resp, reject);
            return;
          }
          let json;
          try {
            json = await resp.json();
          } catch {}
          if (getCookie("refresh_token") === refreshToken) {
            setCookie("access_token", "", -1);
            setCookie("refresh_token", "", -1);
            clearUserLogin();
            useAuthStore().bump();
          }
          reject({
            message: "unable to authenticate",
            description: json?.message,
          });
          return;
        }
        const data: AuthResponse = await resp.json();
        if (getCookie("refresh_token") !== refreshToken) {
          resolve(null);
          return;
        }
        setCookie("access_token", data.jwt_pair.access, 7);
        setCookie("refresh_token", data.jwt_pair.refresh, 30);
        useAuthStore().bump();
        resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async getAuthMethods(signal?: AbortSignal) {
    return new Promise<{ type: ProviderType }[]>(async (resolve, reject) => {
      try {
        const resp = await fetch("/api/v1/auth-methods", { signal });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },

  doLogin(request: AuthRequest, signal?: AbortSignal) {
    return new Promise<AuthResponse>(async (resolve, reject) => {
      try {
        const resp = await fetch("/api/v1/login", {
          method: "POST",
          body: JSON.stringify(request),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async addToBlacklist(item: BlacklistItemType, signal?: AbortSignal) {
    return new Promise<string[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/blacklist", {
          method: "POST",
          body: JSON.stringify(item),
          signal,
        });
        if (!resp.ok) {
          try {
            if (
              resp.headers.get("Content-Type")?.includes("application/json")
            ) {
              const json = await resp.json();
              reject({
                status: resp.status,
                message: json?.message ?? "Unknown error",
              });
            } else {
              const text = await resp.text();
              reject({ status: resp.status, message: text });
            }
          } catch (err) {
            reject({ status: resp.status, message: err });
          }
          return;
        }
        const json = await resp.json();
        resolve(json.items);
      } catch (e) {
        reject(e);
      }
    });
  },
  async removeFromBlacklist(id: string, signal?: AbortSignal) {
    return new Promise<string[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/blacklist", {
          method: "DELETE",
          body: JSON.stringify({ items: [id] }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getFlows(signal?: AbortSignal) {
    return new Promise<FlowType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/flows/listall", { signal });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async searchFlowsByUuid(uuid: string, signal?: AbortSignal) {
    return new Promise<FlowType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/flows/listall?${listallQuery([uuid], false)}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async searchFlowsByUuids(uuids: string[], signal?: AbortSignal) {
    return new Promise<FlowType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/flows/listall?${listallQuery(uuids, false)}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async searchFlowsByExternalId(externalId: string, signal?: AbortSignal) {
    return new Promise<FlowType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/flows/listall?${listallQuery([externalId], false)}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async searchFlowsByAgentId(agentId: string, signal?: AbortSignal) {
    return new Promise<FlowType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/flows/listall?${listallQuery([agentId], true)}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async searchFlowsByGroupId(groupId: string, signal?: AbortSignal) {
    return new Promise<FlowType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/flows/listall?${listallQuery([groupId], true)}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getSpecialFlows(id: string, signal?: AbortSignal) {
    return new Promise<FlowType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/flows/listall?ids=${encodeURIComponent(id)}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async addFlow(flow: FlowType, signal?: AbortSignal) {
    return new Promise<string[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/flows", {
          method: "POST",
          body: JSON.stringify(flow),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async listFlows(conditions: ConditionsFlows, signal?: AbortSignal) {
    return new Promise<{ items: FlowType[]; total: number }>(
      async (resolve, reject) => {
        if (!(await checkAccessToken(reject))) return;
        try {
          const resp = await fetch("/api/v1/hosts/flows/list", {
            method: "POST",
            body: JSON.stringify(conditions),
            signal,
          });
          if (!resp.ok) processError(resp, reject);
          else {
            const json = await resp.json();
            resolve(json);
          }
        } catch (e) {
          reject(e);
        }
      },
    );
  },
  async patchFlow(patch: PatchFlowType, signal?: AbortSignal) {
    return new Promise<FlowType>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/flows", {
          method: "PATCH",
          body: JSON.stringify(patch),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(await resp.json());
      } catch (e) {
        reject(e);
      }
    });
  },
  async getBlacklist(signal?: AbortSignal) {
    return new Promise<BlacklistItemType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/blacklist/listall", { signal });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async turnOffAgents(agent_ids: string[], signal?: AbortSignal) {
    return new Promise<string[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/turn_off", {
          method: "POST",
          body: JSON.stringify({ items: agent_ids }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getHosts(signal?: AbortSignal) {
    return new Promise<HostType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/hosts/listall", { signal });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getHostsSummary(signal?: AbortSignal) {
    return new Promise<HostsSummary>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/hosts/summary", { signal });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve({
            active: Number(json.active ?? 0),
            monitoring: Number(json.monitoring ?? 0),
            enforcing: Number(json.enforcing ?? 0),
            dead: Number(json.dead ?? 0),
            total: Number(json.total ?? 0),
          });
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getSelectedHosts(items: string[], signal?: AbortSignal) {
    return new Promise<HostType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/hosts/list", {
          method: "POST",
          body: JSON.stringify({ items: items }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getSelectedHostsByAgentId(items: string[], signal?: AbortSignal) {
    return new Promise<HostType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/hosts/listagent", {
          method: "POST",
          body: JSON.stringify({ items: items }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getSearchedHostsByIfaceId(ifaceId: string, signal?: AbortSignal) {
    return new Promise<HostType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/hosts/search/iface?q=${ifaceId}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getSearchedHostsByLabel(label: string, signal?: AbortSignal) {
    return new Promise<HostType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/hosts/search/label?q=${label}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async setCluster(clusterType: any, signal?: AbortSignal) {
    return new Promise<clusterGroup[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/interfaces/cluster", {
          method: "POST",
          body: JSON.stringify(clusterType),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.groups as clusterGroup[]);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getInterfaces(signal?: AbortSignal) {
    return new Promise<HostInterfaceType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/interfaces/list", {
          method: "POST",
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getGroups(signal?: AbortSignal) {
    return new Promise<HostGroupType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/groups/listall", { signal });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getSelectedGroups(items: string[], signal?: AbortSignal) {
    return new Promise<HostGroupType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/groups/list", {
          method: "POST",
          body: JSON.stringify({ items: items }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async addGroup(group: HostGroupType, signal?: AbortSignal) {
    return new Promise<string[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/groups", {
          method: "POST",
          body: JSON.stringify({ group: group, segments: [] }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async modifyGroup(group: HostGroupType, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/groups", {
          method: "PUT",
          body: JSON.stringify(group),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteGroups(IDs: string[], signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/groups`, {
          method: "DELETE",
          body: JSON.stringify({ items: IDs }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteRule(id: string, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/flows?items=${id}`, {
          method: "DELETE",
          body: JSON.stringify({ items: [id] }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteAllRules(ids: string[], signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/flows`, {
          method: "DELETE",
          body: JSON.stringify({ items: [...ids] }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async getExternalHosts(signal?: AbortSignal) {
    return new Promise<ExternalHostType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/cidrs/listall`, { signal });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getSelectedExternalsHosts(items: string[], signal?: AbortSignal) {
    return new Promise<ExternalHostType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch("/api/v1/hosts/cidrs/external", {
          method: "POST",
          body: JSON.stringify({ items: items }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async addExternalHost(host: ExternalHostType, signal?: AbortSignal) {
    return new Promise<string[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/external`, {
          method: "POST",
          body: JSON.stringify(host),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async updateExternalHost(host: ExternalHostType, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/external`, {
          method: "PUT",
          body: JSON.stringify(host),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteExternalHosts(IDs: string[], signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/external`, {
          method: "DELETE",
          body: JSON.stringify({ items: IDs }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  // async addExternalData(host: ExternalHostType) {

  // }
  async getUsersList(signal?: AbortSignal) {
    return new Promise<UserType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/users`, { signal });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async addUser(user: RegisterRequestWithRoles, signal?: AbortSignal) {
    return new Promise<AddUserResponse>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/users`, {
          method: "POST",
          body: JSON.stringify(user),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async registerUser(user: AuthRequest, signal?: AbortSignal) {
    return new Promise<AuthResponse>(async (resolve, reject) => {
      try {
        const resp = await fetch(`/api/v1/register`, {
          method: "POST",
          body: JSON.stringify(user),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async updateUserRole(userID: string, role: string, signal?: AbortSignal) {
    return new Promise<string[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/role`, {
          method: "PUT",
          body: JSON.stringify({
            role: role,
            userId: userID,
          }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteUser(userID: string, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/users/${userID}`, {
          method: "DELETE",
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async setUserPassword(
    userID: string,
    password: string,
    signal?: AbortSignal,
  ) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/users/${userID}/password`, {
          method: "PUT",
          body: JSON.stringify({ password }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async getSegmentsList(signal?: AbortSignal) {
    return new Promise<SegmentType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/segments/listall`, { signal });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async addSegment(segment: SegmentType, signal?: AbortSignal) {
    return new Promise<string[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/segments`, {
          method: "POST",
          body: JSON.stringify(segment),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async updateSegment(segment: SegmentType, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/segments`, {
          method: "PUT",
          body: JSON.stringify(segment),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteSegments(segmentIDs: string[], signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/segments`, {
          method: "DELETE",
          body: JSON.stringify({ items: segmentIDs }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async addAuthMethod(provider: AuthProvider, signal?: AbortSignal) {
    return new Promise<string[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/auth-methods`, {
          method: "PUT",
          body: JSON.stringify(provider),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteAuthMethod(provider: ProviderType, signal?: AbortSignal) {
    return new Promise<string[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/auth-methods/${provider}`, {
          method: "DELETE",
          body: JSON.stringify(provider),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getFirewallEvents(
    options?: FirewallEventsFetchOptions,
    signal?: AbortSignal,
  ) {
    return new Promise<FirewallEventsResponse>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      const params: Record<string, string> = {};
      if (options?.agentId !== undefined) params["agent"] = options.agentId;
      if (options?.limit !== undefined)
        params["limit"] = options.limit.toString();
      if (options?.offset !== undefined)
        params["offset"] = options.offset.toString();
      if (options?.fromDttm !== undefined)
        params["from_dttm"] = dateToUtcStartOfDay(options.fromDttm);
      if (options?.toDttm !== undefined)
        params["to_dttm"] = dateToUtcEndOfDay(options.toDttm);
      if (options?.comm) params["comm"] = options.comm;
      if (options?.fwType) params["fw_type"] = options.fwType;
      if (options?.hostSearch) params["host_search"] = options.hostSearch;
      if (options?.port !== undefined) params["port"] = options.port.toString();
      if (options?.username) params["username"] = options.username;
      if (options?.compact) params["compact"] = options.compact;
      if (options?.sortBy) params["sort_by"] = options.sortBy;
      if (options?.packetsThreshold !== undefined)
        params["packets_threshold"] = options.packetsThreshold.toString();
      if (options?.alertClass) params["alert_class"] = options.alertClass;
      if (options?.alertLevel) params["alert_level"] = options.alertLevel;
      if (options?.alertDescription)
        params["alert_description"] = options.alertDescription;
      if (options?.onlyAffected !== undefined)
        params["only_affected"] = String(options.onlyAffected);
      if (options?.attachment) params["attachment"] = options.attachment;
      if (options?.onlyNotMarkedAsRead !== undefined)
        params["only_not_marked_as_read"] = String(options.onlyNotMarkedAsRead);
      const query = new URLSearchParams(params).toString();
      try {
        const resp = await fetch(`/api/v1/events/firewall?${query}`, {
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getKProbeEvents(
    agentId?: string,
    limit?: number,
    offset?: number,
    fromDttm?: string,
    toDttm?: string,
    signal?: AbortSignal,
  ) {
    return new Promise<{ items: KprobeEvent[]; total_count: number }>(
      async (resolve, reject) => {
        if (!(await checkAccessToken(reject))) return;
        const params: Record<string, string> = {};
        if (agentId !== undefined) params["agent"] = agentId;
        if (limit !== undefined) params["limit"] = limit.toString();
        if (offset !== undefined) params["offset"] = offset.toString();
        if (fromDttm !== undefined)
          params["from_dttm"] = new Date(fromDttm).toISOString();
        if (toDttm !== undefined)
          params["to_dttm"] = new Date(toDttm).toISOString();
        const query = new URLSearchParams(params).toString();
        try {
          const resp = await fetch(`/api/v1/events/kprobe?${query}`, {
            signal,
          });
          if (!resp.ok) processError(resp, reject);
          else {
            const json = await resp.json();
            resolve(json);
          }
        } catch (e) {
          reject(e);
        }
      },
    );
  },

  async getProcessEvents(
    agentId?: string,
    limit?: number,
    offset?: number,
    fromDttm?: string,
    toDttm?: string,
    processName?: string,
    signal?: AbortSignal,
  ) {
    return new Promise<{ items: ProcessEvent[]; total_count: number }>(
      async (resolve, reject) => {
        if (!(await checkAccessToken(reject))) return;
        const params: Record<string, string> = {};
        if (agentId) params["agent"] = agentId;
        if (limit !== undefined) params["limit"] = limit.toString();
        if (offset !== undefined) params["offset"] = offset.toString();
        if (fromDttm !== undefined)
          params["from_dttm"] = dateToUtcStartOfDay(fromDttm);
        if (toDttm !== undefined) params["to_dttm"] = dateToUtcEndOfDay(toDttm);
        if (processName) params["comm"] = processName;
        const query = new URLSearchParams(params).toString();
        try {
          const resp = await fetch(`/api/v1/events/process?${query}`, {
            signal,
          });
          if (!resp.ok) processError(resp, reject);
          else {
            const json = await resp.json();
            resolve(json);
          }
        } catch (e) {
          reject(e);
        }
      },
    );
  },
  async getNetflowEvents(
    agentId?: string,
    limit?: number,
    offset?: number,
    fromDttm?: string,
    toDttm?: string,
    signal?: AbortSignal,
  ) {
    return new Promise<{ items: NetflowEvent[]; total_count: number }>(
      async (resolve, reject) => {
        if (!(await checkAccessToken(reject))) return;
        const params: Record<string, string> = {};
        if (agentId !== undefined) params["agent"] = agentId;
        if (limit !== undefined) params["limit"] = limit.toString();
        if (offset !== undefined) params["offset"] = offset.toString();
        if (fromDttm !== undefined)
          params["from_dttm"] = dateToUtcStartOfDay(fromDttm);
        if (toDttm !== undefined) params["to_dttm"] = dateToUtcEndOfDay(toDttm);
        const query = new URLSearchParams(params).toString();
        try {
          const resp = await fetch(`/api/v1/events/netflow?${query}`, {
            signal,
          });
          if (!resp.ok) processError(resp, reject);
          else {
            const json = await resp.json();
            resolve(json);
          }
        } catch (e) {
          reject(e);
        }
      },
    );
  },
  async getConversationsGraph(
    agentId?: string,
    limit?: number,
    offset?: number,
    fromDttm?: string,
    toDttm?: string,
    comm?: string,
    port?: string,
    signal?: AbortSignal,
  ) {
    return new Promise<{ items: ConversationItem[]; total_count: number }>(
      async (resolve, reject) => {
        if (!(await checkAccessToken(reject))) return;
        const params: Record<string, string> = {};
        if (agentId) params["agent"] = agentId;
        if (limit !== undefined) params["limit"] = limit.toString();
        if (offset !== undefined) params["offset"] = offset.toString();
        if (fromDttm) params["from_dttm"] = dateToUtcStartOfDay(fromDttm);
        if (toDttm) params["to_dttm"] = dateToUtcEndOfDay(toDttm);
        if (comm) params["comm"] = comm;
        if (port) params["port"] = port;
        const query = new URLSearchParams(params).toString();
        try {
          const resp = await fetch(`/api/v1/events/conversations?${query}`, {
            signal,
          });
          if (!resp.ok) processError(resp, reject);
          else {
            const json = await resp.json();
            resolve(json);
          }
        } catch (e) {
          reject(e);
        }
      },
    );
  },
  async getAlerts(
    agentId?: string,
    limit?: number,
    offset?: number,
    fromDttm?: string,
    toDttm?: string,
    level?: AlertLevel,
    alertClass?: AlertClass,
    description?: string,
    signal?: AbortSignal,
  ) {
    return new Promise<{ items: Alert[]; total_count: number }>(
      async (resolve, reject) => {
        if (!(await checkAccessToken(reject))) return;
        const params: Record<string, string> = {};
        if (agentId !== undefined) params["agent"] = agentId;
        if (limit !== undefined) params["limit"] = limit.toString();
        if (offset !== undefined) params["offset"] = offset.toString();
        if (fromDttm !== undefined)
          params["from_dttm"] = dateToUtcStartOfDay(fromDttm);
        if (toDttm !== undefined) params["to_dttm"] = dateToUtcEndOfDay(toDttm);
        if (level !== undefined) params["alert_level"] = level;
        if (alertClass !== undefined) params["alert_class"] = alertClass;
        if (description) params["alert_description"] = description;
        const query = new URLSearchParams(params).toString();
        try {
          const resp = await fetch(`/api/v1/events/alerts?${query}`, {
            signal,
          });
          if (!resp.ok) processError(resp, reject);
          else {
            const json = await resp.json();
            resolve(json);
          }
        } catch (e) {
          reject(e);
        }
      },
    );
  },
  async getAlertsWithReadMark(
    agentId?: string,
    limit?: number,
    offset?: number,
    fromDttm?: string,
    toDttm?: string,
    level?: AlertLevel,
    alertClass?: AlertClass,
    description?: string,
    readMark?: boolean,
    signal?: AbortSignal,
  ) {
    return requestAlertsWithReadMark(
      {
        agentId,
        limit,
        offset,
        fromDttm,
        toDttm,
        level,
        alertClass,
        description,
        onlyUnread: readMark,
      },
      signal,
    );
  },
  // `/events/alerts` only. Unread badges on the network map stay on
  // getAlertsWithReadMark / getUnreadAlerts so iptables/nftables are not
  // filtered out there.
  async getAlertsPageWithReadMark(
    options?: Omit<AlertsWithReadMarkParams, "fwType">,
    signal?: AbortSignal,
  ) {
    return requestAlertsWithReadMark(
      { ...options, fwType: "FIREWALL_TYPE_OUR_FW" },
      signal,
    );
  },
  async getUnreadAlerts(
    fromDttm?: string,
    toDttm?: string,
    isOnlyUnread?: boolean,
    signal?: AbortSignal,
  ) {
    return this.getAlertsWithReadMark(
      undefined,
      undefined,
      undefined,
      fromDttm,
      toDttm,
      undefined,
      undefined,
      undefined,
      isOnlyUnread,
      signal,
    );
  },
  async markAlertsAsRead(items: string[], signal?: AbortSignal) {
    return new Promise(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/events/alerts/mark-as-read`, {
          method: "POST",
          body: JSON.stringify({ items }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async getNotificationRules(signal?: AbortSignal) {
    return new Promise<NotificationRule[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/notificationcenter/rules/listall`, {
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async addNotificationRule(rule: NotificationRule, signal?: AbortSignal) {
    return new Promise<string>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/notificationcenter/rules`, {
          method: "POST",
          body: JSON.stringify(rule),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.item);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteNotificationRule(id: string, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/notificationcenter/rules`, {
          method: "DELETE",
          body: JSON.stringify({
            item: id,
          }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async getNotificationTransports(signal?: AbortSignal) {
    return new Promise<NotificationTransport[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/notificationcenter/transports/listall`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async addNotificationTransport(
    transport: NotificationTransport,
    signal?: AbortSignal,
  ) {
    return new Promise<string>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/notificationcenter/transports`, {
          method: "POST",
          body: JSON.stringify(transport),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.item);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteNotificationTransport(id: string, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/notificationcenter/transports`, {
          method: "DELETE",
          body: JSON.stringify({ item: id }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async updateNotificationTransport(
    transport: NotificationTransport,
    signal?: AbortSignal,
  ) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/notificationcenter/transports`, {
          method: "PUT",
          body: JSON.stringify(transport),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async getFlowTemplates(signal?: AbortSignal) {
    return new Promise<FlowTemplate[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/flow_templates/listall`, {
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async addFlowTemplate(template: FlowTemplate, signal?: AbortSignal) {
    for (let i = 0; i < template.flows.items.length; i++) {
      if (template.flows.items[i].src == "") template.flows.items[i].src = "{}";
      else if (template.flows.items[i].dst == "")
        template.flows.items[i].dst = "{}";
    }
    return new Promise<string[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/flow_templates`, {
          method: "POST",
          body: JSON.stringify(template),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteFlowTemplates(IDs: string[], signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/flow_templates`, {
          method: "DELETE",
          body: JSON.stringify({ items: IDs }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async applyFlowTemplate(
    templateId: string,
    hostId: string,
    signal?: AbortSignal,
  ) {
    return new Promise<FlowType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/flow_templates/apply`, {
          method: "POST",
          body: JSON.stringify({
            template: templateId,
            uuid: hostId,
          }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getAgentParameters(signal?: AbortSignal) {
    return new Promise<AgentParameters>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/agent_parameters`, { signal });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async applyAgentParameter(params: AgentParameters, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/agent_parameters`, {
          method: "PUT",
          body: JSON.stringify(params),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },

  async getHostSettings(agentId: string, signal?: AbortSignal) {
    return new Promise(async (resolve, reject) => {
      try {
        const resp = await fetch(`/api/v1/hosts/caps?item=${agentId}`, {
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async forceRestrictedAgent(items: string[], signal?: AbortSignal) {
    return new Promise<string[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/force_restricted`, {
          method: "POST",
          body: JSON.stringify({ items }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items ?? []);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async getLicenseInfo(signal?: AbortSignal) {
    return new Promise<LicenseInfo>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/license`, { signal });
        if (!resp.ok) {
          await processError(resp, reject);
          return;
        }
        const json = await resp.json();
        // The gateway omits zero/empty fields, so normalize defensively.
        resolve({
          org: json.org ?? "",
          email: json.email ?? "",
          from: json.from ?? "",
          until: json.until ?? "",
          agents: Number(json.agents ?? 0),
          agents_count: Number(json.agents_count ?? 0),
        });
      } catch (e) {
        reject(e);
      }
    });
  },
  async getUserParameters(signal?: AbortSignal) {
    return new Promise<UserParameters>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/users/meta`, { signal });
        if (!resp.ok) {
          if (resp.status == 401) reject("unauthorized");
          // Normalize to ApiHttpError so callers can react to the status
          // (e.g. userSettingsStore.load() retries a 429).
          else await processError(resp, reject);
          return;
        }
        const json = await resp.json();
        if (json.meta == "") {
          const userParameters = emptyUserParameters();
          this.putUserParameter(userParameters);
          resolve(userParameters);
          return;
        }
        try {
          let metaJSON = JSON.parse(json.meta);
          const defaults = emptyUserParameters();
          if (
            !(
              "layout_info" in metaJSON &&
              "graph_settings" in metaJSON &&
              "locale" in metaJSON &&
              "is_tutorial_passed" in metaJSON
            )
          ) {
            this.putUserParameter(defaults);
            metaJSON = defaults;
          }
          resolve({
            ...defaults,
            ...metaJSON,
            // Nested snapshots are often `{}` in mocks and old accounts.
            layout_info: {
              ...defaults.layout_info,
              ...(metaJSON.layout_info ?? {}),
            },
            graph_settings: mergeGraphSettings(
              defaults.graph_settings,
              metaJSON.graph_settings,
            ),
          });
        } catch {
          const userParameters = emptyUserParameters();
          this.putUserParameter(userParameters);
          resolve(userParameters);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async setUserParameters(params: UserParameters, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/users/meta`, {
          method: "POST",
          body: JSON.stringify({ meta: JSON.stringify(params) }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async putUserParameter(
    params: Partial<UserParameters>,
    signal?: AbortSignal,
    opts?: { keepalive?: boolean },
  ) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/users/meta`, {
          method: "PUT",
          body: JSON.stringify({ meta: JSON.stringify(params) }),
          signal,
          // keepalive lets the request outlive a page unload (used by the
          // meta-PUT flush on pagehide/visibilitychange). Body is small.
          keepalive: opts?.keepalive === true,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async getHostsPath(id1: string, id2: string, signal?: AbortSignal) {
    return new Promise<string[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/path?first=${id1}&second=${id2}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async resetPassword(email: string, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      try {
        const resp = await fetch(`/api/v1/reset-password`, {
          method: "POST",
          body: JSON.stringify({ email }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async changePasswordViaReset(
    token: string,
    password: string,
    signal?: AbortSignal,
  ) {
    return new Promise<null>(async (resolve, reject) => {
      try {
        const resp = await fetch(`/api/v1/reset-password/${token}`, {
          method: "POST",
          body: JSON.stringify({ password }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async getExcludes(signal?: AbortSignal) {
    return new Promise<ExcludeType[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/trustmng/excludes/listall`, {
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async addFirewallExclude(exclude: FirewallExclude, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/trustmng/excludes/firewall`, {
          method: "POST",
          body: JSON.stringify(exclude),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteExcludes(IDs: string[], signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/trustmng/excludes`, {
          method: "DELETE",
          body: JSON.stringify({ items: IDs }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async getLabels(signal?: AbortSignal) {
    return new Promise<HostTag[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/labels/listall`, { signal });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async updateHostLabels(id: string, tags: string[], signal?: AbortSignal) {
    return new Promise<HostType>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/hosts`, {
          method: "PUT",
          body: JSON.stringify({
            id: id,
            info: { labels: tags },
          }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async changePassword(oldPass: string, newPass: string, signal?: AbortSignal) {
    return new Promise(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/change-password`, {
          method: "POST",
          body: JSON.stringify({
            old_password: oldPass,
            new_password: newPass,
          }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async getRecommendations(
    options?: ListRecommendationsParams,
    signal?: AbortSignal,
  ) {
    return new Promise<Recommendations>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      const params = new URLSearchParams();
      if (options?.with_rejected !== undefined)
        params.set("with_rejected", String(options.with_rejected));
      if (options?.only_pending !== undefined)
        params.set("only_pending", String(options.only_pending));
      if (options?.source) params.set("source", options.source);
      if (options?.sort_by) params.set("sort_by", options.sort_by);
      if (options?.limit !== undefined)
        params.set("limit", String(options.limit));
      if (options?.offset !== undefined)
        params.set("offset", String(options.offset));
      if (options?.expand !== undefined)
        params.set("expand", String(options.expand));
      if (options?.strict !== undefined)
        params.set("strict", String(options.strict));
      for (const id of options?.endpoints ?? []) params.append("endpoints", id);
      for (const id of options?.ids ?? []) params.append("ids", id);
      const query = params.toString();
      try {
        const resp = await fetch(
          `/api/v1/hosts/flows_recommendations/listall?${query}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async searchRecommendationsByUuid(uuid: string, signal?: AbortSignal) {
    return new Promise<Recommendations>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/flows_recommendations/listall?${listallQuery([uuid], false, { with_rejected: "true" })}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async searchRecommendationsByUuids(uuids: string[], signal?: AbortSignal) {
    return new Promise<Recommendations>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/flows_recommendations/listall?${listallQuery(uuids, false, { with_rejected: "true" })}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async searchRecommendationsByExternalId(
    externalId: string,
    signal?: AbortSignal,
  ) {
    return new Promise<Recommendations>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/flows_recommendations/listall?${listallQuery([externalId], false, { with_rejected: "true" })}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async searchRecommendationsByAgentId(agentId: string, signal?: AbortSignal) {
    return new Promise<Recommendations>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/flows_recommendations/listall?${listallQuery([agentId], true, { with_rejected: "true" })}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async searchRecommendationsByGroupId(groupId: string, signal?: AbortSignal) {
    return new Promise<Recommendations>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(
          `/api/v1/hosts/flows_recommendations/listall?${listallQuery([groupId], true, { with_rejected: "true" })}`,
          { signal },
        );
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async acceptRecommendations(
    payload: AcceptRecommendationsPayload,
    signal?: AbortSignal,
  ) {
    return new Promise<{ flows: FlowType[]; externals: ExternalHostType[] }>(
      async (resolve, reject) => {
        if (!(await checkAccessToken(reject))) return;
        try {
          const resp = await fetch(
            `/api/v1/hosts/flows_recommendations/accept`,
            {
              method: "POST",
              body: JSON.stringify(payload),
              signal,
            },
          );
          if (!resp.ok) processError(resp, reject);
          else {
            const json = await resp.json();
            resolve(json);
          }
        } catch (e) {
          reject(e);
        }
      },
    );
  },
  async rejectRecommendations(IDs: string[], signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/flows_recommendations/reject`, {
          method: "POST",
          body: JSON.stringify({ items: IDs }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async addLabel(tag: HostTag, hostIds: string[] = [], signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/labels`, {
          method: "POST",
          // LabelAssignment{name,color,host_ids}: the tag name is HostTag.id.
          body: JSON.stringify({
            name: tag.id,
            color: tag.color,
            host_ids: hostIds,
          }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async updateLabel(
    tag: HostTag,
    hostIds: string[] = [],
    signal?: AbortSignal,
  ) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/labels`, {
          method: "PUT",
          // host_ids is add-only here (never detaches); detach is host-side via updateHostLabels.
          body: JSON.stringify({
            name: tag.id,
            color: tag.color,
            host_ids: hostIds,
          }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteLabels(ids: string[], signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/labels`, {
          method: "DELETE",
          body: JSON.stringify({ items: ids }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteDeadHosts(IDs: string[], signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/hosts`, {
          method: "DELETE",
          body: JSON.stringify({ items: IDs }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async getOperationLogs(
    fromDttm: string,
    toDttm: string,
    user?: string,
    opType?: string,
    signal?: AbortSignal,
    page?: number,
    size?: number,
    search?: string,
  ) {
    return new Promise<{ items: OperationLogs[]; total: number }>(
      async (resolve, reject) => {
        if (!(await checkAccessToken(reject))) return;
        const params = new URLSearchParams({
          from_dttm: fromDttm,
          to_dttm: toDttm,
        });
        if (user !== undefined) {
          params.append("user_login", user);
        }
        if (opType !== undefined) {
          params.append("op", opType);
        }
        if (page !== undefined) {
          params.append("page", page.toString());
        }
        if (size !== undefined) {
          params.append("size", size.toString());
        }
        if (search) {
          params.append("q", search);
        }
        try {
          const resp = await fetch(`/api/v1/hosts/oplog?${params.toString()}`, {
            signal,
          });
          if (!resp.ok) processError(resp, reject);
          else {
            const json = await resp.json();
            resolve(json);
          }
        } catch (e) {
          reject(e);
        }
      },
    );
  },
  async revertOperationLogs(items: string[], signal?: AbortSignal) {
    return new Promise<OperationLogs[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/oplog/revert`, {
          method: "POST",
          body: JSON.stringify({ items: items }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async setModuleStatesToAgents(
    agents: string[],
    moduleStates: string,
    signal?: AbortSignal,
  ) {
    return new Promise<OperationLogs[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/module_states`, {
          method: "POST",
          body: JSON.stringify({ module_states: moduleStates, agents: agents }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async fuzzySearch(data: FuzzySearchRequest, signal?: AbortSignal) {
    return new Promise<FuzzySearchItem[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/fuzzy`, {
          method: "POST",
          body: JSON.stringify(data),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async listAiChats(limit = 50, offset = 0, signal?: AbortSignal) {
    return new Promise<AiChatDTO[]>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/ai/chats/list`, {
          method: "POST",
          body: JSON.stringify({ limit, offset }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else {
          const json = await resp.json();
          resolve(json.items ?? []);
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  async uploadAiImage(file: File, signal?: AbortSignal) {
    return new Promise<{ id: string }>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const buf = await file.arrayBuffer();
        let binary = "";
        const bytes = new Uint8Array(buf);
        for (let i = 0; i < bytes.length; i++)
          binary += String.fromCharCode(bytes[i]);
        const resp = await fetch(`/api/v1/hosts/ai/images`, {
          method: "POST",
          body: JSON.stringify({ data: btoa(binary), mime: file.type }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(await resp.json());
      } catch (e) {
        reject(e);
      }
    });
  },
  async getAiChat(id: string, signal?: AbortSignal) {
    return new Promise<AiChatDTO>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/ai/chats/get`, {
          method: "POST",
          body: JSON.stringify({ id }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(await resp.json());
      } catch (e) {
        reject(e);
      }
    });
  },
  async deleteAiChat(id: string, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/ai/chats/delete`, {
          method: "POST",
          body: JSON.stringify({ id }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async updateAiChatTitle(id: string, title: string, signal?: AbortSignal) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const resp = await fetch(`/api/v1/hosts/ai/chats/title`, {
          method: "POST",
          body: JSON.stringify({ id, title }),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
  async setAiProposalStatus(
    chatId: string,
    toolCallId: string,
    status: "accepted" | "rejected" | "failed",
    errorMessage?: string,
    editedArguments?: string,
    userNote?: string,
    appliedIds?: string[],
    snapshot?: string,
    signal?: AbortSignal,
  ) {
    return new Promise<null>(async (resolve, reject) => {
      if (!(await checkAccessToken(reject))) return;
      try {
        const body: Record<string, unknown> = {
          chat_id: chatId,
          tool_call_id: toolCallId,
          status,
        };
        if (errorMessage) body.error_message = errorMessage;
        if (editedArguments) body.edited_arguments = editedArguments;
        if (userNote) body.user_note = userNote;
        if (appliedIds?.length) body.applied_ids = appliedIds;
        if (snapshot) body.snapshot = snapshot;
        const resp = await fetch(`/api/v1/hosts/ai/chats/proposal_status`, {
          method: "POST",
          body: JSON.stringify(body),
          signal,
        });
        if (!resp.ok) processError(resp, reject);
        else resolve(null);
      } catch (e) {
        reject(e);
      }
    });
  },
};
